import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Badge from '@/components/common/Badge'
import Button from '@/components/common/Button'

function DispatchHistory({ team }) {
  const [dispatches, setDispatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    if (!team?.id) return

    const fetchDispatches = async () => {
      try {
        const { data, error } = await supabase
          .from('dispatches')
          .select('id, title, sent_at, recipient_count')
          .eq('team_id', team.id)
          .order('sent_at', { ascending: false })
          .limit(showAll ? 100 : 10)

        if (error) throw error
        setDispatches(data || [])
      } catch (err) {
        console.error('Error fetching dispatches:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchDispatches()
  }, [team?.id, showAll])

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    })
  }

  const toggleExpand = async (dispatchId) => {
    if (expandedId === dispatchId) {
      setExpandedId(null)
      return
    }

    // Fetch full content if not already loaded
    if (expandedId !== dispatchId) {
      try {
        const { data, error } = await supabase
          .from('dispatches')
          .select('content')
          .eq('id', dispatchId)
          .single()

        if (error) throw error
        
        // Update the dispatch in state with full content
        setDispatches(prev => prev.map(d => 
          d.id === dispatchId ? { ...d, content: data.content } : d
        ))
      } catch (err) {
        console.error('Error fetching dispatch content:', err)
      }
    }
    
    setExpandedId(dispatchId)
  }

  if (loading) {
    return (
      <Card>
        <h2 className="font-heading text-lg font-bold mb-4">Recent Dispatches</h2>
        <div className="text-center text-brand-slate py-4">Loading...</div>
      </Card>
    )
  }

  if (dispatches.length === 0) {
    return (
      <Card>
        <h2 className="font-heading text-lg font-bold mb-4">Recent Dispatches</h2>
        <div className="text-center text-brand-slate py-6">
          <p className="mb-2">No dispatches sent yet</p>
          <p className="text-sm">Your sent dispatches will appear here</p>
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold">Recent Dispatches</h2>
        <Badge variant="gray">{dispatches.length}</Badge>
      </div>

      <div className="space-y-2">
        {dispatches.map((dispatch) => (
          <div 
            key={dispatch.id}
            className="border border-border rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggleExpand(dispatch.id)}
              className="w-full p-3 text-left hover:bg-brand-cream/40 transition"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-brand-charcoal truncate mb-1 break-words">
                    {dispatch.title || 'Untitled Dispatch'}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-brand-slate flex-wrap">
                    <span>{formatDate(dispatch.sent_at)}</span>
                    <span>•</span>
                    <span>{dispatch.recipient_count || 0} recipients</span>
                  </div>
                </div>
                <div className="text-brand-slate text-sm flex-shrink-0">
                  {expandedId === dispatch.id ? '▼' : '▶'}
                </div>
              </div>
            </button>
            
            {expandedId === dispatch.id && dispatch.content && (
              <div className="px-3 pb-3 border-t border-border bg-white">
                <div 
                  className="prose prose-sm max-w-none pt-3"
                  dangerouslySetInnerHTML={{ __html: dispatch.content }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {!showAll && dispatches.length >= 10 && (
        <div className="mt-4 text-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowAll(true)}
          >
            View All Dispatches
          </Button>
        </div>
      )}
    </Card>
  )
}

export default DispatchHistory

